// src/services/friendService.js
// Couche service — opérations Firebase pour le système d'amis

import { db, functions } from '../firebase';
import { collection, query, where, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const friendService = {
  // ── Cloud Functions callables ────────────────────────────

  async sendFriendRequest(toUid) {
    try {
      const fn = httpsCallable(functions, 'sendFriendRequest');
      const result = await fn({ toUid });
      return result.data;
    } catch (err) {
      console.error('friendService.sendFriendRequest error:', err);
      return { success: false, error: err.message || 'Erreur envoi demande' };
    }
  },

  async acceptFriendRequest(requestId) {
    try {
      const fn = httpsCallable(functions, 'acceptFriendRequest');
      const result = await fn({ requestId });
      return result.data;
    } catch (err) {
      console.error('friendService.acceptFriendRequest error:', err);
      return { success: false, error: err.message || 'Erreur acceptation demande' };
    }
  },

  async declineFriendRequest(requestId) {
    try {
      const fn = httpsCallable(functions, 'declineFriendRequest');
      const result = await fn({ requestId });
      return result.data;
    } catch (err) {
      console.error('friendService.declineFriendRequest error:', err);
      return { success: false, error: err.message || 'Erreur refus demande' };
    }
  },

  async removeFriend(friendUid) {
    try {
      const fn = httpsCallable(functions, 'removeFriend');
      const result = await fn({ friendUid });
      return result.data;
    } catch (err) {
      console.error('friendService.removeFriend error:', err);
      return { success: false, error: err.message || 'Erreur suppression ami' };
    }
  },

  // ── Queries Firestore directes ──────────────────────────

  /**
   * Récupère les demandes reçues en attente.
   * Index composites requis : friendRequests (toUid ASC, status ASC, createdAt DESC)
   */
  async getPendingRequestsReceived(uid) {
    try {
      const q = query(
        collection(db, 'friendRequests'),
        where('toUid', '==', uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      return {
        success: true,
        data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
    } catch (err) {
      console.error('friendService.getPendingRequestsReceived error:', err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Récupère les demandes envoyées en attente.
   * Index composites requis : friendRequests (fromUid ASC, status ASC)
   */
  async getPendingRequestsSent(uid) {
    try {
      const q = query(
        collection(db, 'friendRequests'),
        where('fromUid', '==', uid),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      return {
        success: true,
        data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
    } catch (err) {
      console.error('friendService.getPendingRequestsSent error:', err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Souscription temps réel aux demandes reçues en attente.
   * Index composites requis : friendRequests (toUid ASC, status ASC, createdAt DESC)
   */
  subscribeToPendingRequests(uid, callback) {
    const q = query(
      collection(db, 'friendRequests'),
      where('toUid', '==', uid),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(requests);
    });
  },

  /**
   * Détermine le statut d'amitié entre deux utilisateurs.
   * Retourne : "friends" | "pending_sent" | "pending_received" | "none"
   */
  async getFriendshipStatus(currentUid, targetUid) {
    try {
      // Vérifier pending envoyée
      const sentQ = query(
        collection(db, 'friendRequests'),
        where('fromUid', '==', currentUid),
        where('toUid', '==', targetUid),
        where('status', '==', 'pending')
      );
      const sentSnap = await getDocs(sentQ);
      if (!sentSnap.empty) return { success: true, data: 'pending_sent' };

      // Vérifier pending reçue
      const receivedQ = query(
        collection(db, 'friendRequests'),
        where('fromUid', '==', targetUid),
        where('toUid', '==', currentUid),
        where('status', '==', 'pending')
      );
      const receivedSnap = await getDocs(receivedQ);
      if (!receivedSnap.empty) {
        return {
          success: true,
          data: 'pending_received',
          requestId: receivedSnap.docs[0].id,
        };
      }

      return { success: true, data: 'none' };
    } catch (err) {
      console.error('friendService.getFriendshipStatus error:', err);
      return { success: false, error: err.message, data: 'none' };
    }
  },
};

export default friendService;
